/**
 * Next Train Arrival Module
 * 
 * Fetches and displays next train arrival times from TTC NTAS API
 * Integrates with the existing drawer system and includes cooldown functionality
 */

import { iconFont } from './icon-fonts.js';

// Cache for storing train data with timestamps
const trainDataCache = new Map();
const NT_CACHE_DURATION = 60000; // 60 seconds cooldown

// Track last refresh times for cooldown enforcement
const lastRefreshTimes = new Map();
const REFRESH_COOLDOWN = 10000; // 10 seconds cooldown for manual refresh

// Auto-refresh functionality
const autoRefreshIntervals = new Map();
const AUTO_REFRESH_INTERVAL = 60000; // 60 seconds auto-refresh

// Use direct object reference comparison (most reliable for interchange stations)
const LINE_MAPPING = {
  line1University: 'Line 1 University',
  line1Yonge: 'Line 1 Yonge',
  line2: 'Line 2',
  line4: 'Line 4',
  line5: 'Line 5',
  line6: 'Line 6',
};

/**
 * Get station codes for a given station
 * @param {Object} station - Station object
 * @param {string} svgId - Optional SVG ID to determine line context
 * @returns {Array} - Array of station codes with direction info
 */
function getStationCodes(station, svgId = null) {
    // Special handling: Disable next train times for Union stations
    // if (station.name === 'Union') {
    //     console.log(`🚇 Union: Next train times disabled for this station`);
    //     return [];
    // }

    const codes = [];

    // Check for eastbound/westbound codes (Line 2)
    if (station.eastbound && station.eastbound.code) {
        codes.push({
            code: station.eastbound.code,
            direction: 'Eastbound',
            line: 'Line 2'
        });
    }

    if (station.westbound && station.westbound.code) {
        codes.push({
            code: station.westbound.code,
            direction: 'Westbound',
            line: 'Line 2'
        });
    }

    // Check for northbound/southbound codes (Line 1, Line 4, Line 5, Line 6)
    if (station.northbound && station.northbound.code) {
        // Special handling for VMC - skip northbound trains as it's the northern terminal
        if (station.name === 'Vaughan Metropolitan Centre') {
            console.log(`🚇 VMC: Skipping northbound code (terminal station - only southbound trains serve passengers)`);
        } else {
            codes.push({
                code: station.northbound.code,
                direction: 'Northbound',
                line: getStationLine(station, svgId)
            });
        }
    }

    if (station.southbound && station.southbound.code) {
        // Special handling for Union station - relabel southbound as northbound
        let directionLabel = 'Southbound';

        if (station.name === 'Union') {
            // At Union: "southbound" trains are actually traveling north to Finch/VMC
            directionLabel = 'Northbound';
            console.log(`🚇 Union special case: Labeling southbound code as "Northbound" (trains to Finch/VMC)`);
        }

        codes.push({
            code: station.southbound.code,
            direction: directionLabel,
            line: getStationLine(station, svgId)
        });
    }

    // Explicitly ignore 'subway' direction as requested
    // Terminal stations have both a directional code (e.g., eastbound) and a 'subway' code
    // We only want the directional codes for next train times
    if (station.subway && station.subway.code) {
        console.log(`🚇 Ignoring subway code ${station.subway.code} for ${station.name} (terminal station)`);
    }

    // Log the final codes for debugging terminal stations
    if (codes.length === 1) {
        console.log(`🚉 Terminal station ${station.name}: Using ${codes.length} direction(s):`, codes.map(c => c.direction).join(', '));
    }

    return codes;
}

/**
 * Determine which line a station belongs to
 * @param {Object} station - Station object
 * @param {string} svgId - Optional SVG ID to determine line context
 * @returns {string} - Line name
 */
function getStationLine(station, svgId = null) {
    // If SVG ID is provided, ALWAYS use it to determine the line
    // This is important for interchange stations shown in multiple SVGs
    if (svgId) {
        const SVG_ID_TO_LINE_KEY = {
            'line1university-svg': LINE_MAPPING.line1University,
            'line1-university-svg': LINE_MAPPING.line1University,

            'line1yonge-svg': LINE_MAPPING.line1Yonge,
            'line1-yonge-svg': LINE_MAPPING.line1Yonge,

            'line2-svg': LINE_MAPPING.line2,
            'line2-bloor-danforth-svg': LINE_MAPPING.line2,

            'line4-svg': LINE_MAPPING.line4,
            'line4-sheppard-svg': LINE_MAPPING.line4,

            'line5-svg': LINE_MAPPING.line5,
            'line5-eglinton-svg': LINE_MAPPING.line5,

            'line6-svg': LINE_MAPPING.line6,
            'line6-finch-west-svg': LINE_MAPPING.line6,
        };

        const lineFromSvg = SVG_ID_TO_LINE_KEY[svgId];
        if (lineFromSvg) {
            console.log(`🚇 Using line from SVG ID: ${svgId} -> ${lineFromSvg}`);
            return lineFromSvg;
        } else {
            console.log(`⚠️ SVG ID ${svgId} not found in mapping, falling back to station object`);
        }
    }

    // Use direct object reference comparison (most reliable for interchange stations)
    const lineMapping = [
        [LINE_MAPPING.line1University, window.line1University],
        [LINE_MAPPING.line1Yonge, window.line1Yonge],
        [LINE_MAPPING.line2, window.line2],
        [LINE_MAPPING.line4, window.line4],
        [LINE_MAPPING.line5, window.line5],
        [LINE_MAPPING.line6, window.line6],
    ];

    for (const [label, line] of lineMapping) {
        if (!line?.stations) continue;

        // Check if this exact station object is in the line
        if (line.stations.includes(station)) {
            console.log(`🚇 Station ${station.name} found in ${label} (direct reference)`);
            return label;
        }
    }

    // Fallback: search by name
    for (const [label, line] of lineMapping) {
        if (!line?.stations) continue;

        if (line.stations.some(s => s.name === station.name)) {
            console.log(`🚇 Station ${station.name} found in ${label} (name match)`);
            return label;
        }
    }

    console.log(`🚇 Station ${station.name} not found in any line, defaulting to Line 1`);
    return 'Line 1';
}

/**
 * Generate a unique cache key for a station that includes line information
 * @param {Object} station - Station object
 * @param {string} connectionLine - Optional connection line name
 * @returns {string} - Unique cache key
 */
function generateStationCacheKey(station, connectionLine = null) {
    // Determine which line this station belongs to using direct object reference
    let lineKey = 'unknown';

    const lineMapping = Object.keys(LINE_MAPPING).map(key => [LINE_MAPPING[key], window[key]]);
    for (const lineName of lineMapping) {
        const line = window[lineName];
        if (!line?.stations) continue;

        // Use direct object reference comparison (most reliable for interchange stations)
        if (line.stations.includes(station)) {
            lineKey = lineName;
            console.log(`🔑 Cache key: Station ${station.name} found in ${lineName}`);
            break;
        }
    }

    if (lineKey === 'unknown') {
        console.log(`⚠️ Cache key: Station ${station.name} not found in any line by reference, using name-based fallback`);
        // Fallback to name-based matching if direct reference fails
        for (const lineName of lineMapping) {
            const line = window[lineName];
            if (!line?.stations) continue;

            if (line.stations.some(s => s.name === station.name)) {
                lineKey = lineName;
                console.log(`🔑 Cache key: Station ${station.name} found in ${lineName} (name match)`);
                break;
            }
        }
    }
    
    // Generate cache key with line information
    if (connectionLine) {
        return `${station.name}@${lineKey}-${connectionLine}`;
    }
    return `${station.name}@${lineKey}`;
}

/**
 * Check if station data is cached and still valid
 * @param {string|Object} stationInput - Station name or station object
 * @param {string} connectionLine - Optional connection line name
 * @returns {Object|null} - Cached data or null if expired/missing
 */
function getCachedTrainData(stationInput, connectionLine = null) {
    let cacheKey;

    if (typeof stationInput === 'object' && stationInput !== null) {
        cacheKey = generateStationCacheKey(stationInput, connectionLine);
    } else {
        // Fallback for string input (backward compatibility)
        if (connectionLine) {
            cacheKey = `${stationInput}-${connectionLine}`;
        } else {
            cacheKey = stationInput;
        }
    }

    const cached = trainDataCache.get(cacheKey);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp < NT_CACHE_DURATION) {
        return cached.data;
    }

    // Remove expired cache
    trainDataCache.delete(cacheKey);
    return null;
}

/**
 * Cache train data for a station
 * @param {string|Object} stationInput - Station name or station object
 * @param {Object} data - Train data to cache
 * @param {string} connectionLine - Optional connection line name
 */
function cacheTrainData(stationInput, data, connectionLine = null) {
    let cacheKey;
    
    if (typeof stationInput === 'object' && stationInput !== null) {
        cacheKey = generateStationCacheKey(stationInput, connectionLine);
    } else {
        // Fallback for string input (backward compatibility)
        if (connectionLine) {
            cacheKey = `${stationInput}-${connectionLine}`;
        } else {
            cacheKey = stationInput;
        }
    }
    
    trainDataCache.set(cacheKey, {
        data: data,
        timestamp: Date.now()
    });
}

/**
 * Fetch next train times directly from TTC NTAS API with server fallback
 * @param {Array} codes - Array of station codes
 * @returns {Promise<Object>} - Train arrival data
 */
async function fetchNextTrainTimes(codes) {
    if (!codes || codes.length === 0) {
        return { success: false, error: 'No station codes available' };
    }

    const codeString = codes.map(c => c.code).join('|');

    // First, try the direct TTC API
    try {
        const directUrl = `https://ntas.ttc.ca/api/ntas/get-next-train-time/${codeString}`;
        console.log(`🚇 Trying direct TTC API: ${directUrl}`);

        const response = await fetch(directUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            mode: 'cors',
            // Add a timeout to prevent hanging
            signal: AbortSignal.timeout(10000) // 10 second timeout
        });

        if (!response.ok) {
            throw new Error(`Direct API HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log(`✅ Direct TTC API successful`);

        return {
            success: true,
            data: data,
            codes: codes,
            source: 'direct'
        };

    } catch (directError) {
        console.warn(`⚠️ Direct TTC API failed: ${directError.message}`);
        console.log(`🔄 Falling back to server proxy...`);

        // Fallback to server proxy
        try {
            const serverUrl = `/api/next-train/${codeString}`;
            console.log(`🚇 Trying server proxy: ${serverUrl}`);

            const response = await fetch(serverUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Server proxy HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log(`✅ Server proxy successful`);

            return {
                success: true,
                data: data,
                codes: codes,
                source: 'server'
            };

        } catch (serverError) {
            console.error('❌ Both direct API and server proxy failed');
            console.error('Direct API error:', directError.message);
            console.error('Server proxy error:', serverError.message);

            return {
                success: false,
                error: `Both direct API and server failed. Direct: ${directError.message}, Server: ${serverError.message}`
            };
        }
    }
}

/**
 * Format train arrival time
 * @param {number} minutes - Minutes until arrival
 * @returns {string} - Formatted time string
 */
function formatArrivalTime(minutes) {
    if (minutes <= 0) {
        return 'Arriving';
    } else if (minutes === 1) {
        return '1 min';
    } else {
        return `${minutes} mins`;
    }
}

/**
 * Clean up and improve destination text for better clarity
 * @param {string} destinationText - Raw destination text from TTC API
 * @returns {string} - Cleaned up destination text
 */
function cleanDestinationText(destinationText) {
    if (!destinationText) return '';

    // Clean up Line 1 Yonge southbound destination text (VMC branch)
    if (destinationText.includes('Vaughan Metropolitan Centre via Union')) {
        return 'Southbound to Union, continuing to Vaughan Metropolitan Centre';
    }

    // Clean up Line 1 University southbound destination text (Finch branch)
    if (destinationText.includes('Finch via Union') || destinationText.includes('Finch Station via Union')) {
        return 'Southbound to Union, continuing to Finch Station';
    }

    // Clean up other common patterns if needed
    if (destinationText.includes('via Union')) {
        return destinationText.replace('via Union', 'through Union to');
    }

    // Return original text if no specific cleanup needed
    return destinationText;
}

/**
 * Process and format train data for display
 * @param {Array} apiData - Raw API response (array of train data)
 * @param {Array} codes - Station codes with direction info
 * @returns {Object} - Formatted train data
 */
function processTrainData(apiData, codes) {
    const processed = {
        directions: [],
        hasData: false
    };

    if (!apiData || !Array.isArray(apiData)) {
        return processed;
    }

    // Group data by direction
    codes.forEach(codeInfo => {
        const stationData = apiData.find(station =>
            station.stopCode === codeInfo.code
        );

        if (stationData && stationData.hasOwnProperty('nextTrains')) {
            // Parse the nextTrains string (e.g., "0, 3, 11")
            // Handle empty strings by showing "No trains scheduled"
            if (stationData.nextTrains === "" || !stationData.nextTrains) {
                // Empty nextTrains - show direction with no trains message
                processed.directions.push({
                    direction: codeInfo.direction,
                    line: codeInfo.line,
                    trains: [],
                    destination: stationData.directionText,
                    noTrains: true
                });
                processed.hasData = true;
            } else {
                const trainTimes = stationData.nextTrains
                    .split(',')
                    .map(time => parseInt(time.trim()))
                    .filter(time => !isNaN(time))
                    .slice(0, 3); // Show up to 3 trains

                if (trainTimes.length > 0) {
                    const trains = trainTimes.map(minutes => ({
                        destination: stationData.directionText || 'Unknown',
                        minutes: minutes,
                        formatted: formatArrivalTime(minutes)
                    }));

                    processed.directions.push({
                        direction: codeInfo.direction,
                        line: codeInfo.line,
                        trains: trains,
                        destination: stationData.directionText
                    });

                    processed.hasData = true;
                } else {
                    // nextTrains had data but no valid times
                    processed.directions.push({
                        direction: codeInfo.direction,
                        line: codeInfo.line,
                        trains: [],
                        destination: stationData.directionText,
                        noTrains: true
                    });
                    processed.hasData = true;
                }
            }
        }
    });

    return processed;
}

/**
 * Update the next train display in the drawer
 * @param {string} stationName - Station name
 * @param {Object} trainData - Processed train data
 */
function updateNextTrainDisplay(stationName, trainData) {
    const containerId = `next-train-container-${stationName.replace(/\s+/g, '-').toLowerCase()}`;
    const container = document.getElementById(containerId);

    if (!container) {
        console.warn(`Next train container not found: ${containerId}`);
        return;
    }

    const timesElement = container.querySelector('.next-train-times');
    if (!timesElement) {
        console.warn('Next train times element not found');
        return;
    }

    if (!trainData.hasData) {
        timesElement.innerHTML = '<span class="text-gray-500">No train data available</span>';
        return;
    }

    // Build HTML for train times
    let html = '';

    trainData.directions.forEach((direction, index) => {
        if (index > 0) html += '<br>';

        html += `<div class="mb-2">
            <div class="text-sm font-medium text-blue-700 mb-1">${direction.direction}</div>
        `;

        if (direction.destination) {
            const cleanedDestination = cleanDestinationText(direction.destination);
            html += `<div class="text-xs text-gray-600 mb-1">${cleanedDestination}</div>`;
        }

        if (direction.trains.length > 0) {
            const trainTimes = direction.trains.map(train =>
                `<span class="inline-block mr-3 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">${train.formatted}</span>`
            ).join('');
            html += `<div>${trainTimes}</div>`;
        } else if (direction.noTrains) {
            html += '<div class="text-gray-500 text-sm">No trains scheduled</div>';
        } else {
            html += '<div class="text-gray-500 text-sm">No trains</div>';
        }

        html += '</div>';
    });

    timesElement.innerHTML = html;

    // Add last updated timestamp
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
    });

    // Add or update timestamp
    let timestampElement = container.querySelector('.next-train-timestamp');
    if (!timestampElement) {
        timestampElement = document.createElement('div');
        timestampElement.className = 'next-train-timestamp text-xs text-gray-400 mt-1';
        container.appendChild(timestampElement);
    }
    timestampElement.textContent = `Updated: ${timeString}`;
}

/**
 * Load next train data for a station
 * @param {Object} station - Station object
 */
async function loadNextTrainData(station, svgId = null) {
    if (!station || !station.name) {
        console.warn('Invalid station object provided');
        return;
    }

    console.log(`🚇 Loading next train data for: ${station.name}`);

    // Check cache first
    const cachedData = getCachedTrainData(station);
    if (cachedData) {
        console.log(`📋 Using cached data for ${station.name}`);
        updateNextTrainDisplay(station.name, cachedData);
        return;
    }

    // Get station codes with SVG ID context
    const codes = getStationCodes(station, svgId);
    if (codes.length === 0) {
        console.warn(`No station codes found for ${station.name}`);
        updateNextTrainDisplay(station.name, { hasData: false });
        return;
    }

    console.log(`📍 Found ${codes.length} direction(s) for ${station.name}:`, codes);

    // Show loading state
    const containerId = `next-train-container-${station.name.replace(/\s+/g, '-').toLowerCase()}`;
    const container = document.getElementById(containerId);
    if (container) {
        const timesElement = container.querySelector('.next-train-times');
        if (timesElement) {
            timesElement.innerHTML = '<span class="text-blue-600">Loading...</span>';
        }
    }

    // Fetch data from API
    const result = await fetchNextTrainTimes(codes);

    if (!result.success) {
        console.error(`❌ Failed to fetch train data for ${station.name}:`, result.error);
        updateNextTrainDisplay(station.name, { hasData: false });
        return;
    }

    // Process and display data
    const processedData = processTrainData(result.data, codes);

    // Cache the processed data
    cacheTrainData(station, processedData);

    // Update display
    updateNextTrainDisplay(station.name, processedData);

    console.log(`✅ Next train data loaded for ${station.name}`);
}

/**
 * Find station object for a specific line by name
 * @param {string} stationName - Station name
 * @param {string} lineName - Line name (e.g., "Line 1", "Line 2", "Line 4")
 * @returns {Object|null} - Station object or null if not found
 */
function findStationByLineAndName(stationName, lineName) {
    let lineData = null;
    let searchName = stationName;

    // Handle stations with different names on different lines
    // Bloor-Yonge interchange: "Bloor" on Line 1, "Yonge" on Line 2
    if (stationName === 'Bloor' && lineName === 'Line 2') {
        searchName = 'Yonge';
        console.log(`🚇 Connection mapping: Looking for "${searchName}" on ${lineName} (from "${stationName}")`);
    } else if (stationName === 'Yonge' && (lineName === 'Line 1' || lineName === 'Line 1 University' || lineName === 'Line 1 Yonge')) {
        searchName = 'Bloor';
        console.log(`🚇 Connection mapping: Looking for "${searchName}" on ${lineName} (from "${stationName}")`);
    }
    // Sheppard-Yonge interchange: "Sheppard-Yonge" on both Line 1 and Line 4
    else if (stationName === 'Sheppard-Yonge') {
        // Keep the same name for both lines
        searchName = 'Sheppard-Yonge';
        console.log(`🚇 Connection mapping: Looking for "${searchName}" on ${lineName} (from "${stationName}")`);
    }

    // Map line names to data objects
    switch (lineName) {
        case 'Line 1':
        case LINE_MAPPING.line1University:
            // Check both University and Yonge branches
            lineData = window.line1University?.stations?.find(s => s.name === searchName) ||
                window.line1Yonge?.stations?.find(s => s.name === searchName);
            break;
        case LINE_MAPPING.line1Yonge:
            lineData = window.line1Yonge?.stations?.find(s => s.name === searchName);
            break;
        case LINE_MAPPING.line2:
            lineData = window.line2?.stations?.find(s => s.name === searchName);
            break;
        case LINE_MAPPING.line4:
            lineData = window.line4?.stations?.find(s => s.name === searchName);
            break;
        case LINE_MAPPING.line5:
            lineData = window.line5?.stations?.find(s => s.name === searchName);
            break;
        case LINE_MAPPING.line6:
            lineData = window.line6?.stations?.find(s => s.name === searchName);
            break;
    }

    return lineData || null;
}

/**
 * Get next train times for a specific connection line at a station
 * @param {Object} station - Station object
 * @param {string} connectionLine - Line name (e.g., "Line 1", "Line 2", "Line 4")
 * @returns {Promise<Object>} - Processed train data for the connection
 */
async function getConnectionTrainTimes(station, connectionLine) {
    if (!station || !connectionLine) {
        return { hasData: false, directions: [] };
    }

    // Check cache first - use station object for unique cache key
    const cachedData = getCachedTrainData(station, connectionLine);
    if (cachedData) {
        return cachedData;
    }

    // Find the station object for the connection line
    const connectionStation = findStationByLineAndName(station.name, connectionLine);

    if (!connectionStation) {
        console.warn(`❌ Could not find station ${station.name} on ${connectionLine}`);
        const noData = { hasData: false, directions: [] };
        cacheTrainData(station, noData, connectionLine);
        return noData;
    }

    // Get codes for the connection station
    const codes = getStationCodes(connectionStation);

    if (codes.length === 0) {
        console.warn(`❌ No station codes found for ${station.name} on ${connectionLine}`);
        const noData = { hasData: false, directions: [] };
        cacheTrainData(station, noData, connectionLine);
        return noData;
    }

    try {
        const result = await fetchNextTrainTimes(codes);

        if (!result.success) {
            const noData = { hasData: false, directions: [] };
            cacheTrainData(station, noData, connectionLine);
            return noData;
        }

        const processedData = processTrainData(result.data, codes);
        cacheTrainData(station, processedData, connectionLine);
        return processedData;

    } catch (error) {
        console.error(`❌ Error fetching connection train data for ${station.name} ${connectionLine}:`, error);
        const noData = { hasData: false, directions: [] };
        cacheTrainData(station, noData, connectionLine);
        return noData;
    }
}

/**
 * Format connection train times for display - split into two halves by direction
 * @param {Object} trainData - Processed train data
 * @returns {string} - HTML string for train times
 */
function formatConnectionTrainTimes(trainData) {
    if (!trainData.hasData || trainData.directions.length === 0) {
        return '<span class="text-xs text-gray-400 ml-2">No data</span>';
    }

    // Create a container with two halves
    let html = '<div class="flex w-full text-xs">';

    // Process each direction separately
    trainData.directions.forEach((direction, index) => {
        // Add separator between directions
        if (index > 0) {
            html += '<div class="w-px bg-gray-300 mx-1"></div>';
        }

        // Create half container for this direction
        html += `<div class="flex-1 ${index === 0 ? 'pr-1' : 'pl-1'}">`;

        // Direction label
        html += `<div class="text-gray-600 font-medium mb-1">${direction.direction}</div>`;

        // Train times for this direction
        if (direction.trains && direction.trains.length > 0) {
            // Show up to 2 trains for this direction
            const trains = direction.trains.slice(0, 2);
            const trainSpans = trains.map(train =>
                `<span class="inline-block mr-1 mb-1 px-1 py-0.5 bg-gray-600 text-white rounded text-xs font-medium">${train.formatted}</span>`
            ).join('');
            html += `<div>${trainSpans}</div>`;
        } else {
            html += '<div class="text-gray-400">No trains</div>';
        }

        html += '</div>';
    });

    // If only one direction, fill the other half
    if (trainData.directions.length === 1) {
        html += '<div class="w-px bg-gray-300 mx-1"></div>';
        html += '<div class="flex-1 pl-1">';
        html += '<div class="text-gray-400 text-xs">—</div>';
        html += '</div>';
    }

    html += '</div>';
    return html;
}

/**
 * Update connection train times in the drawer
 * @param {Object} station - Station object
 */
async function updateConnectionTrainTimes(station) {
    if (!station.connections || station.connections.length === 0) {
        return;
    }

    console.log(`🚇 Updating connection train times for: ${station.name}`);

    // Skip connection train times for Union station
    if (station.name === 'Union') {
        console.log(`🚇 Union: Skipping connection train times display`);
        // Process each connection but leave times blank
        for (const connection of station.connections) {
            const connectionId = `connection-${station.name.replace(/\s+/g, '-').toLowerCase()}-${connection.replace(/\s+/g, '-').toLowerCase()}`;
            const connectionElement = document.getElementById(connectionId);

            if (connectionElement) {
                const existingTimes = connectionElement.querySelector('.connection-train-times');
                if (existingTimes) {
                    existingTimes.innerHTML = ''; // Leave blank
                }
            }
        }
        return;
    }

    // Process each connection
    for (const connection of station.connections) {
        const connectionId = `connection-${station.name.replace(/\s+/g, '-').toLowerCase()}-${connection.replace(/\s+/g, '-').toLowerCase()}`;
        const connectionElement = document.getElementById(connectionId);

        if (connectionElement) {
            // Show loading state
            const existingTimes = connectionElement.querySelector('.connection-train-times');
            if (existingTimes) {
                existingTimes.innerHTML = '<span class="text-xs text-gray-400 ml-2">Loading...</span>';
            }

            try {
                // Get train times for this connection
                const trainData = await getConnectionTrainTimes(station, connection);
                const formattedTimes = formatConnectionTrainTimes(trainData);

                // Update the display
                if (existingTimes) {
                    existingTimes.innerHTML = formattedTimes;
                }
            } catch (error) {
                console.error(`❌ Error updating connection times for ${connection}:`, error);
                if (existingTimes) {
                    existingTimes.innerHTML = '<span class="text-xs text-gray-400 ml-2">Error</span>';
                }
            }
        }
    }
}

/**
 * Refresh train times for a specific station (manual refresh with cooldown)
 * @param {Object|string} stationInput - Station object or station name (for backward compatibility)
 */
async function refreshTrainTimes(stationInput) {
    // Handle both station object and string inputs for backward compatibility
    let station = null;
    let stationName = '';

    if (typeof stationInput === 'object' && stationInput !== null) {
        // Station object passed directly
        station = stationInput;
        stationName = station.name;
    } else if (typeof stationInput === 'string') {
        // Station name passed as string - search for it
        stationName = stationInput;
        const dataMap = window.stationData;

        for (const [key, branch] of Object.entries(dataMap)) {
            if (branch && branch.stations) {
                const foundStation = branch.stations.find(s => s.name === stationName);
                if (foundStation) {
                    station = foundStation;
                    break;
                }
            }
        }
    }

    if (!station) {
        console.error(`❌ Station not found: ${stationName}`);
        return;
    }

    const now = Date.now();
    const lastRefresh = lastRefreshTimes.get(stationName) || 0;
    const timeSinceLastRefresh = now - lastRefresh;

    // Check if cooldown period has passed
    if (timeSinceLastRefresh < REFRESH_COOLDOWN) {
        const remainingTime = Math.ceil((REFRESH_COOLDOWN - timeSinceLastRefresh) / 1000);
        console.log(`⏳ Refresh cooldown active for ${stationName}. Wait ${remainingTime} more seconds.`);

        // Update button to show remaining time
        const buttonId = `refresh-train-times-${stationName.replace(/\s+/g, '-').toLowerCase()}`;
        const button = document.getElementById(buttonId);
        if (button) {
            const originalContent = button.innerHTML;
            button.disabled = true;
            button.innerHTML = `${iconFont('clock', 'text-sm')}<span>${remainingTime}s</span>`;

            // Re-enable button after remaining time
            setTimeout(() => {
                button.disabled = false;
                button.innerHTML = originalContent;
            }, remainingTime * 1000);
        }
        return;
    }

    // Update last refresh time
    lastRefreshTimes.set(stationName, now);

    console.log(`🔄 Manual refresh triggered for: ${stationName}`);

    // Clear cache for this station to force fresh data
    // Use station object to generate proper cache key with line information
    const cacheKey = generateStationCacheKey(station);
    trainDataCache.delete(cacheKey);

    // Clear connection caches too
    if (station.connections) {
        station.connections.forEach(connection => {
            const connectionCacheKey = generateStationCacheKey(station, connection);
            trainDataCache.delete(connectionCacheKey);
        });
    }

    // Update button to show loading state
    const buttonId = `refresh-train-times-${stationName.replace(/\s+/g, '-').toLowerCase()}`;
    const button = document.getElementById(buttonId);
    if (button) {
        const originalContent = button.innerHTML;
        button.disabled = true;
        button.innerHTML = `<div class="animate-spin rounded-full h-3 w-3 border-b border-white"></div><span>Updating...</span>`;

        // Load fresh data
        try {
            await loadNextTrainData(station);

            // Also refresh connection times
            setTimeout(async () => {
                await updateConnectionTrainTimes(station);
            }, 200);

            // Reset button after successful refresh
            setTimeout(() => {
                button.disabled = false;
                button.innerHTML = originalContent;
            }, 1000);

            console.log(`✅ Manual refresh completed for: ${stationName}`);

        } catch (error) {
            console.error(`❌ Manual refresh failed for ${stationName}:`, error);

            // Reset button on error
            setTimeout(() => {
                button.disabled = false;
                button.innerHTML = originalContent;
            }, 2000);
        }
    }
}

/**
 * Start auto-refresh for a station (refreshes every 60 seconds)
 * @param {Object} station - Station object
 */
function startAutoRefresh(station) {
    if (!station || !station.name) return;

    // Clear any existing auto-refresh for this station
    stopAutoRefresh(station.name);

    console.log(`🔄 Starting auto-refresh for: ${station.name} (every 60 seconds)`);

    const intervalId = setInterval(async () => {
        console.log(`⏰ Auto-refresh triggered for: ${station.name}`);

        // Clear cache to force fresh data
        trainDataCache.delete(station.name);

        // Clear connection caches too
        if (station.connections) {
            station.connections.forEach(connection => {
                const cacheKey = `${station.name}-${connection}`;
                trainDataCache.delete(cacheKey);
            });
        }

        try {
            // Refresh main train data
            await loadNextTrainData(station);

            // Refresh connection times
            setTimeout(async () => {
                await updateConnectionTrainTimes(station);
            }, 200);

            console.log(`✅ Auto-refresh completed for: ${station.name}`);

        } catch (error) {
            console.error(`❌ Auto-refresh failed for ${station.name}:`, error);
        }
    }, AUTO_REFRESH_INTERVAL);

    // Store the interval ID so we can clear it later
    autoRefreshIntervals.set(station.name, intervalId);
}

/**
 * Stop auto-refresh for a station
 * @param {string} stationName - Station name
 */
function stopAutoRefresh(stationName) {
    const intervalId = autoRefreshIntervals.get(stationName);
    if (intervalId) {
        clearInterval(intervalId);
        autoRefreshIntervals.delete(stationName);
        console.log(`🛑 Auto-refresh stopped for: ${stationName}`);
    }
}

/**
 * Stop all auto-refresh intervals (called when drawer closes)
 */
function stopAllAutoRefresh() {
    autoRefreshIntervals.forEach((intervalId, stationName) => {
        clearInterval(intervalId);
        console.log(`🛑 Auto-refresh stopped for: ${stationName}`);
    });
    autoRefreshIntervals.clear();
}

/**
 * Initialize next train functionality when drawer opens
 * This function should be called when a station drawer is opened
 * @param {Object} station - Station object
 */
function initializeNextTrainForStation(station, svgId = null) {
    // Load train data when drawer opens
    loadNextTrainData(station, svgId);

    // Also load connection train times
    setTimeout(() => {
        updateConnectionTrainTimes(station);
    }, 200); // Small delay to let main train data load first

    // Start auto-refresh after initial load
    setTimeout(() => {
        startAutoRefresh(station);
    }, 1000); // Start auto-refresh 1 second after initial load
}

// Export functions for use in other modules
if (typeof window !== 'undefined') {
    window.nextTrainModule = {
        loadNextTrainData,
        initializeNextTrainForStation,
        getCachedTrainData,
        getStationCodes,
        getConnectionTrainTimes,
        updateConnectionTrainTimes,
        formatConnectionTrainTimes,
        findStationByLineAndName,
        refreshTrainTimes,
        startAutoRefresh,
        stopAutoRefresh,
        stopAllAutoRefresh
    };

    // Also make refreshTrainTimes globally available for onclick handlers
    window.refreshTrainTimes = refreshTrainTimes;
}

console.log('🚇 Next Train module loaded');